import { Fragment, useMemo } from "react";
import { v4 as uuidv4 } from "uuid";
import { faPlusCircle } from "@fortawesome/free-solid-svg-icons/faPlusCircle";
import {
    IdentityProviderAttributeType,
    IdentityProviderAttributeTypeField,
    FormattedIdentityProviderCompanyIDPAttributeMap,
} from "@idw-types/data/identity-providers";
import { IdentityProviderFederatedAttributesErrors } from "@idw-types/common/validation/identity-provider/identity-provider-validation-errors";
import { ButtonWithIcon } from "../../common/button-with-icon/button-with-icon";
import { ContentPageHeader } from "../../common/content-page-header/content-page-header";
import { IdentityProviderFederatedAttributeContainer } from "./identity-provider-federated-attribute-container";
import styles from "../identity-provider-shared-style.less";
import { SelectOption } from "@idw-types/common";
import { resources } from "@constants/hardcoded-strings";
import { ContentPageBody } from "../../common/content-page-body/content-page-body";

type IdentityProviderFederatedAttributesContainerProps = {
    attributeTypes: Array<IdentityProviderAttributeType>;
    attributeTypeFields: Array<IdentityProviderAttributeTypeField>;
    isDisabled: boolean;
    validationErrors: IdentityProviderFederatedAttributesErrors;
    companyIDPAttributeMapsState: Array<FormattedIdentityProviderCompanyIDPAttributeMap>;
    setCompanyIDPAttributeMapsState: (state: Array<FormattedIdentityProviderCompanyIDPAttributeMap>) => void;
};

export const IdentityProviderFederatedAttributesContainer = (props: IdentityProviderFederatedAttributesContainerProps) => {
    const {
        attributeTypes,
        attributeTypeFields,
        isDisabled,
        validationErrors,
        companyIDPAttributeMapsState,
        setCompanyIDPAttributeMapsState
    } = props;

    const handleAddNewParameterClick = () => {
        const newFederatedAttribute: FormattedIdentityProviderCompanyIDPAttributeMap = {
            attributeTypeID: null,
            companyIDPInfoID: null,
            fieldType: null,
            id: null,
            idpField: null,
            uuid: uuidv4()
        };
        setCompanyIDPAttributeMapsState([...companyIDPAttributeMapsState, newFederatedAttribute]);
    };

    const handleIDPFieldChange = (newValue: string, index: number) => {
        const newCompanyIDPAttributeMaps = companyIDPAttributeMapsState.map((param, i) => {
            return index !== i ? param : { ...param, idpField: newValue };
        });
        setCompanyIDPAttributeMapsState(newCompanyIDPAttributeMaps);
    };

    const handleAttributeTypeChange = (newValue: SelectOption, index: number) => {
        const newCompanyIDPAttributeMaps = companyIDPAttributeMapsState.map((param, i) => {
            return index !== i ? param : { ...param, attributeTypeID: newValue.value, fieldType: null };
        });
        setCompanyIDPAttributeMapsState(newCompanyIDPAttributeMaps);
    };

    const handleAttributeTypeFieldChange = (newValue: SelectOption, index: number) => {
        const newCompanyIDPAttributeMaps = companyIDPAttributeMapsState.map((param, i) => {
            return index !== i ? param : { ...param, fieldType: newValue.value };
        });
        setCompanyIDPAttributeMapsState(newCompanyIDPAttributeMaps);
    };

    const handleDeleteClick = (index: number) => {
        const newInfoParams = [...companyIDPAttributeMapsState];
        newInfoParams.splice(index, 1);
        setCompanyIDPAttributeMapsState(newInfoParams);
    };

    const resource = resources.pages.identityProviderDetails.tabs.federatedAttributes;

    const headerButton = (
        <ButtonWithIcon
            id="add-identity-provider-federated-attribute-button"
            icon={faPlusCircle}
            onClick={handleAddNewParameterClick}
            title={resource.headerButton}
            text={resource.headerButton}
            disabled={isDisabled}
        />
    );

    const federatedAttributes = useMemo(() => {
        return companyIDPAttributeMapsState;
    }, [companyIDPAttributeMapsState]);

    return (
        <div className={styles.identityProviderDetailsSection}>
            <ContentPageHeader title={resource.header} headerButton={headerButton} />
            <ContentPageBody isMaxWidth={true}>
                {federatedAttributes.length > 0 ? (
                    <div>
                        {federatedAttributes.map((federatedAttribute, index) => (
                            <Fragment key={`federated-attribute-${federatedAttribute.uuid}`}>
                                <IdentityProviderFederatedAttributeContainer
                                    idPrefix={`federated-attribute-${federatedAttribute.uuid}`}
                                    index={index}
                                    attribute={federatedAttribute}
                                    attributeTypes={attributeTypes}
                                    attributeTypeFields={attributeTypeFields}
                                    isDisabled={isDisabled}
                                    handleIDPFieldChange={handleIDPFieldChange}
                                    handleAttributeTypeChange={handleAttributeTypeChange}
                                    handleAttributeTypeFieldChange={handleAttributeTypeFieldChange}
                                    handleDeleteClick={handleDeleteClick}
                                />
                                {validationErrors.federatedAttributesErrors[federatedAttribute.uuid] || null}
                            </Fragment>
                        ))}
                    </div>
                ) : (
                    <span>No Federated Attributes have been created yet.</span>
                )
                }
            </ContentPageBody>
        </div >
    );
};